import { getBroadcastActivityDetails, getBroadcastRunHistory } from "api/services/communication";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { ResType } from "types";
import { getTitle } from "utils";
import AccessDenied from "../clientGroups/access-denied";
import Loader from "components/Loader";
import { Box, Chip, Divider, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import moment from "moment";

const RecipientCard = ({
  name,
  email,
  sentAt,
}: {
  name: string;
  email: string;
  sentAt?: string;
}) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      px: 2,
      py: 1.5,
    }}
  >
    <Typography variant="body2" fontWeight={500} noWrap>
      {getTitle(name)}
    </Typography>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ wordBreak: "break-all", whiteSpace: "normal" }}
    >
      {email}
    </Typography>
    {sentAt && (
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        {moment(sentAt).utcOffset("+05:30", true).format("DD MMM YYYY, h:mm A")}
      </Typography>
    )}
  </Box>
);

const BroadcastMessageDetails = () => {
  const [accessCheck, setAccessCheck] = useState(true);
  const { broadcastActivityId } = useParams();

  const { data, isLoading }: ResType = useQuery(
    ["broadcastactivitydetails", { id: broadcastActivityId }],
    getBroadcastActivityDetails,
    {
      onSuccess: (res) => {
        setAccessCheck(res?.data?.checkAccess);
      },
    }
  );

  const isRecurring = data?.data?.getOneEmailTemplate?.sendType === "RECURRING";

  const { data: runHistoryData, isLoading: historyLoading }: ResType = useQuery(
    ["broadcast-run-history", { id: broadcastActivityId }],
    getBroadcastRunHistory,
    { enabled: !!broadcastActivityId && isRecurring }
  );

  const groupedRuns = React.useMemo(() => {
    if (!runHistoryData?.data?.length) return [];
    const flat: any[] = runHistoryData.data;

    const map = new Map<string, any[]>();
    flat.forEach((item) => {
      const key =
        item.runNumber != null
          ? String(item.runNumber)
          : moment(item.sentAt).utcOffset("+05:30", true).format("YYYY-MM-DD HH:mm");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });

    const entries = Array.from(map.entries()).sort(([a], [b]) => b.localeCompare(a));
    return entries.map(([, items], idx) => ({
      runNumber: entries.length - idx,
      sentAt: items[0]?.sentAt,
      recipients: items,
    }));
  }, [runHistoryData?.data]);

  if (isLoading) return <Loader />;

  return (
    <>
      {!accessCheck && <AccessDenied />}
      {accessCheck && (
        <Box p={3} pb={0} px={1}>
          {/* Header info cards */}
          <Box display="flex" gap={2} mt={2} mb={3} flexWrap="wrap">
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                minWidth: 220,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Group Name
              </Typography>
              <Typography variant="body2" fontWeight={400} noWrap>
                {data?.data?.getOneEmailTemplate?.groupName || "-"}
              </Typography>
            </Box>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                minWidth: 220,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Email Template Name
              </Typography>
              <Typography variant="body2" fontWeight={400} noWrap>
                {data?.data?.getOneEmailTemplate?.templateName || "-"}
              </Typography>
            </Box>
          </Box>

          {/* ── NORMAL: single recipient section box ── */}
          {!isRecurring && (() => {
            const template = data?.data?.getOneEmailTemplate;
            const details = template?.details || [];
            const sentAt =
              template?.Status === "SENT" ? template?.updatedAt : template?.scheduledAt;
            return (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                {/* Sent At header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {template?.Status === "SENT" ? "Sent At" : "Scheduled At"}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {sentAt
                        ? moment(sentAt).utcOffset("+05:30", true).format("DD MMM YYYY, h:mm A")
                        : "-"}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<SendIcon sx={{ fontSize: 14 }} />}
                    label={`${details.length} emails sent`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5, letterSpacing: 0.5 }}
                >
                  RECIPIENTS
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(auto-fill, minmax(230px, 1fr))"
                  gap={1.5}
                >
                  {details.map((r: any, i: number) => (
                    <RecipientCard key={i} name={r.clientName} email={r.email} />
                  ))}
                </Box>
              </Box>
            );
          })()}

          {/* ── RECURRING: recipients section + grouped send history boxes ── */}
          {isRecurring && (
            <>
              {/* Recipients (visible before any sends happen) */}
              {(() => {
                const details = data?.data?.getOneEmailTemplate?.details || [];
                return (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2.5,
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2">Recipients</Typography>
                      <Chip
                        icon={<SendIcon sx={{ fontSize: 14 }} />}
                        label={`${details.length} clients`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1.5, letterSpacing: 0.5 }}
                    >
                      RECIPIENTS
                    </Typography>
                    <Box
                      display="grid"
                      gridTemplateColumns="repeat(auto-fill, minmax(230px, 1fr))"
                      gap={1.5}
                    >
                      {details.map((r: any, i: number) => (
                        <RecipientCard key={i} name={r.clientName} email={r.email} />
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              {/* Send history — only rendered once sends exist */}
              {!historyLoading && groupedRuns.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" mb={2}>
                    Sent History
                  </Typography>
                </>
              )}

              {historyLoading ? (
                <Loader />
              ) : (
                <Box display="flex" flexDirection="column" gap={2} mt={groupedRuns.length ? 0 : 0}>
                  {groupedRuns.map((run) => (
                    <Box
                      key={run.runNumber}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2.5,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              bgcolor: "primary.main",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700,
                              fontSize: 15,
                              flexShrink: 0,
                            }}
                          >
                            {run.runNumber}
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">
                              Sent #{run.runNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {run.sentAt
                                ? moment(run.sentAt)
                                    .utcOffset("+05:30", true)
                                    .format("DD MMM YYYY, h:mm A")
                                : "-"}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          icon={<SendIcon sx={{ fontSize: 14 }} />}
                          label={`${run.recipients.length} emails sent`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1.5, letterSpacing: 0.5 }}
                      >
                        RECIPIENTS
                      </Typography>

                      <Box
                        display="grid"
                        gridTemplateColumns="repeat(auto-fill, minmax(230px, 1fr))"
                        gap={1.5}
                      >
                        {run.recipients.map((r: any, i: number) => (
                          <RecipientCard key={i} name={r.clientName} email={r.email} />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default BroadcastMessageDetails;
